// Copyright 2025, University of Maryland, All Rights Reserved
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import React from "react"
import type { SectionFieldData } from "@/utils/bill-analysis-parser"

interface SectionCardProps {
  title?: string
  fields: SectionFieldData[]
  icon?: React.ComponentType<any>
  className?: string
}

// Helper function to safely render any value as a string
const renderValue = (value: any): string => {
  if (value === null || value === undefined) {
    return 'N/A'
  }
  
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  
  if (typeof value === 'object') {
    // Handle objects that might be AIImpact objects with {type, score} structure
    if ('type' in value && 'score' in value) {
      return `${value.type} (${value.score})`
    }
    
    // Handle other objects by converting to JSON or extracting meaningful parts
    if (Array.isArray(value)) {
      return value.map(item => renderValue(item)).join(', ')
    }
    
    // For other objects, try to extract meaningful information
    const keys = Object.keys(value)
    if (keys.length === 0) {
      return 'Empty object'
    }
    
    // Show key-value pairs for small objects
    if (keys.length <= 3) {
      return keys.map(key => `${key}: ${renderValue(value[key])}`).join(', ')
    }
    
    // For large objects, just show the keys
    return `Object with keys: ${keys.join(', ')}`
  }
  
  return String(value)
}

const getScoreBadgeStyle = (score_type: string) => {
  const lowerType = score_type.toLowerCase()
  if (lowerType === 'positive') {
    return "bg-green-100 text-green-800 border-green-200 hover:bg-green-200"
  }
  if (lowerType === 'negative') {
    return "bg-red-100 text-red-800 border-red-200 hover:bg-red-200"
  }
  // neutral or any other value
  return "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200"
}

const getEnumBadgeStyle = (value: string) => {
  const lowerValue = value.toLowerCase()
  if (lowerValue.includes('positive') || lowerValue.includes('high') || lowerValue.includes('good')) {
    return "bg-green-100 text-green-800"
  }
  if (lowerValue.includes('negative') || lowerValue.includes('low') || lowerValue.includes('bad')) {
    return "bg-red-100 text-red-800"
  }
  return "bg-gray-100 text-gray-800"
}

const renderFieldValue = (field: SectionFieldData): React.ReactNode => {
  switch (field.type) {
    case 'integer':
      return (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
            {renderValue(field.value)}
          </Badge>
          {field.title && <span className="text-sm font-medium text-muted-foreground">{field.title}</span>}
        </div>
      )
    
    case 'text':
      return (
        <div className="text-sm">
          {field.title && <h4 className="font-semibold text-base mb-2 text-foreground">{field.title}:</h4>}
          <p className="text-muted-foreground leading-relaxed">{renderValue(field.value)}</p>
        </div>
      )
    
    case 'score': {

      const score = field.value?.score ?? 'N/A'
      const score_type = field.value?.score_type ?? 'unknown'

      return (
        <div className="flex items-center gap-3">
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="secondary"
                  className={cn(
                    "font-semibold border cursor-pointer transition-colors",
                    getScoreBadgeStyle(score_type)
                  )}
                  title={`${score_type.charAt(0).toUpperCase() + score_type.slice(1)} Impact`}
                >
                  Score: {score}
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="top" className="z-[60] bg-black text-white">
                <p>{score_type.charAt(0).toUpperCase() + score_type.slice(1)} Impact</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {field.title && <span className="text-sm font-medium text-muted-foreground">{field.title}</span>}
        </div>
      )
    }
    case 'enum': {
      const enumValue = renderValue(field.value)
      return (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className={cn(getEnumBadgeStyle(enumValue))}>
            {enumValue}
          </Badge>
          {field.title && <span className="text-sm font-medium text-muted-foreground">{field.title}</span>}
        </div>
      )
    }
    case 'text_list': {
      const listItems = Array.isArray(field.value) ? field.value : [field.value]
      return (
        <div className="text-sm">
          {field.title && <h4 className="font-semibold text-base mb-3 text-foreground">{field.title}:</h4>}
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            {listItems.map((item: any, index: number) => (
              <li key={index}>{renderValue(item)}</li>
            ))}
          </ul>
        </div>
      )
    }
    
    case 'object_list': {
      const objectItems = Array.isArray(field.value) ? field.value : [field.value]
      
      // Handle empty arrays
      if (Array.isArray(field.value) && field.value.length === 0) {
        return (
          <div className="text-sm">
            {field.title && <h4 className="font-semibold text-base mb-3 text-foreground">{field.title}:</h4>}
            <div className="text-muted-foreground italic text-center py-4 bg-gray-50 rounded-lg border border-gray-200">
              No items found
            </div>
          </div>
        )
      }
      
      return (
        <div className="text-sm">
          {field.title && <h4 className="font-semibold text-base mb-3 text-foreground">{field.title}:</h4>}
          <div className="space-y-3">
            {objectItems.map((obj: any, index: number) => (
              <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                {/* Add index for multiple items */}
                {objectItems.length > 1 && (
                  <div className="text-xs text-muted-foreground mb-2 font-medium">
                    Item {index + 1}
                  </div>
                )}
                <div className="space-y-2">
                  {typeof obj === 'object' && obj !== null ?
                    Object.entries(obj).map(([key, value]) => {
                      // Get the property title from schema, fallback to formatted key
                      const property_title = field.object_properties?.[key]?.title || key.replace(/_/g, ' ')
                      const property_type = field.object_properties?.[key]?.type
                      
                      return (
                        <div key={key} className="text-sm">
                          {/* Check if this is a score field within the object */}
                          {typeof value === 'object' && value !== null && 'score' in value && 'score_type' in value ? (
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-foreground min-w-0 flex-shrink-0">{property_title}:</span>
                              <Badge
                                variant="secondary"
                                className={cn(
                                  "font-semibold border text-xs px-2 py-1",
                                  getScoreBadgeStyle(String(value.score_type))
                                )}
                              >
                                {String(value.score)} ({String(value.score_type)})
                              </Badge>
                            </div>
                          ) : property_type === 'integer' && typeof value === 'number' ? (
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-foreground min-w-0 flex-shrink-0">{property_title}:</span>
                              <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs px-2 py-1">
                                {value}
                              </Badge>
                            </div>
                          ) : Array.isArray(value) ? (
                            <div className="space-y-1">
                              <span className="font-medium text-foreground">{property_title}:</span>
                              <ul className="list-disc list-inside ml-3 space-y-1">
                                {value.map((item: any, itemIndex: number) => (
                                  <li key={itemIndex} className="text-muted-foreground text-sm">
                                    {renderValue(item)}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <div className="flex items-start gap-2">
                              <span className="font-medium text-foreground min-w-0 flex-shrink-0">{property_title}:</span> 
                              <span className="text-muted-foreground break-words">{renderValue(value)}</span>
                            </div>
                          )}
                        </div>
                      )
                    }) :
                    <span className="text-sm text-muted-foreground">{renderValue(obj)}</span>
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }
    default:
      return <span className="text-sm text-muted-foreground">{renderValue(field.value)}</span>
  }
}

export function SectionCard({
  title,
  fields,
  icon: Icon,
  className
}: SectionCardProps) {
  return (
    <Card className={cn("shadow-sm", className)}>
      {title && (
        <CardHeader className="pb-3 p-4">
          <div className="flex items-center gap-3">
            {Icon && <Icon className="h-6 w-6 text-primary" />}
            <CardTitle className="text-2xl font-bold text-foreground">{title}</CardTitle>
          </div>
        </CardHeader>
      )}
      <CardContent className="space-y-4 p-4 pt-0">
        {fields.map((field, index) => (
          <div key={index}>
            {renderFieldValue(field)}
            {index < fields.length - 1 && <Separator className="mt-3" />}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
